/**
 * @file LocalPickupForm.tsx
 *
 * The local-pickup create/edit form, mirroring WSB-v2: a "Pickup options" section
 * (title + instructions) and a "Pickup address" section (street, city + zip,
 * country), each a Gravity {@link ContentContainer} card. The country uses a
 * native `<select>` (the Gravity `Select` web component isn't drivable under
 * jsdom). Owns the editable state via {@link useLocalPickupFormState}, validates
 * the required fields on save, and reports the rebuilt {@link PickupLocationInput} to
 * its parent. Cancelling while dirty asks for confirmation.
 */

import { useId, useMemo, useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { ContentContainer, GeneralField, Icon, Textarea } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { ShopContinent, PickupLocationInput } from '../../api/shop-types';
import { getCountryOptions } from '../shipping-region-utils';
import { ConfirmModal } from '../../components/ConfirmModal';
import { FormFooter } from '../../components/FormFooter';
import { useLocalPickupFormState } from './use-local-pickup-form-state';
import { buildPickupLocation } from './local-pickup-form-utils';
import styles from './LocalPickupForm.module.css';

/** Which flow the form is serving; drives the title and save label. */
export type LocalPickupFormMode = 'create' | 'edit';

interface LocalPickupFormProps {
  mode: LocalPickupFormMode;
  /** The seed input (stable reference; the hook re-seeds when it changes). */
  seed: PickupLocationInput;
  continents: ShopContinent[];
  saving: boolean;
  saveError?: string;
  onSubmit: (location: PickupLocationInput) => void;
  onCancel: () => void;
}

/**
 * Renders the local-pickup form.
 *
 * @param props - Mode, seed location, region tree, save state, and handlers.
 *
 * @returns The pickup form.
 */
export function LocalPickupForm({
  mode,
  seed,
  continents,
  saving,
  saveError,
  onSubmit,
  onCancel,
}: LocalPickupFormProps) {
  const intl = useIntl();
  const fieldId = useId();
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const { fields, patch, isDirty, errors, validate, firstErrorRef } =
    useLocalPickupFormState(seed);

  const countryOptions = useMemo(() => getCountryOptions(continents), [continents]);

  const handleSave = (): void => {
    if (!validate()) {
      return;
    }
    onSubmit(buildPickupLocation(fields, seed));
  };

  const handleCancel = (): void => {
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    onCancel();
  };

  const title =
    mode === 'edit'
      ? adminMessages.shippingPickupEditTitle
      : adminMessages.shippingPickupAddTitle;

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/shipping">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...title} />
        </h2>
        <div className="gv-text-sm">
          <FormattedMessage {...adminMessages.shippingPickupFormDescription} />
        </div>
      </header>

      <div className={`gv-flex gv-flex-col gv-gap-lg gv-w-full ${styles.column}`}>
        {/* Pickup options */}
        <div className="gv-flex gv-flex-col gv-gap-md">
          <div className="gv-text-lg gv-text-bold">
            <FormattedMessage {...adminMessages.shippingPickupOptionsTitle} />
          </div>
          <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-md">
            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={`${fieldId}-name`}>
                <FormattedMessage {...adminMessages.shippingPickupName} />
              </label>
              <GeneralField
                id={`${fieldId}-name`}
                inputRef={firstErrorRef as RefObject<HTMLInputElement>}
                value={fields.name}
                error={errors.nameError}
                data-testid="pickup-form-name"
                placeholder={intl.formatMessage(adminMessages.shippingPickupNamePlaceholder)}
                aria-label={intl.formatMessage(adminMessages.shippingPickupName)}
                onChange={(event) => patch({ name: event.target.value })}
              />
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={`${fieldId}-details`}>
                <FormattedMessage {...adminMessages.shippingPickupDetails} />
              </label>
              <Textarea
                id={`${fieldId}-details`}
                value={fields.details}
                rows={3}
                data-testid="pickup-form-details"
                placeholder={intl.formatMessage(adminMessages.shippingPickupDetailsPlaceholder)}
                aria-label={intl.formatMessage(adminMessages.shippingPickupDetails)}
                onChange={(event) => patch({ details: event.target.value })}
              />
            </div>
          </ContentContainer>
        </div>

        {/* Pickup address */}
        <div className="gv-flex gv-flex-col gv-gap-md">
          <div className="gv-text-lg gv-text-bold">
            <FormattedMessage {...adminMessages.shippingPickupAddressTitle} />
          </div>
          <ContentContainer className="gv-p-lg gv-flex gv-flex-col gv-gap-md">
            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={`${fieldId}-street`}>
                <FormattedMessage {...adminMessages.shippingPickupStreet} />
              </label>
              <GeneralField
                id={`${fieldId}-street`}
                value={fields.address1}
                error={errors.address1Error}
                data-testid="pickup-form-street"
                aria-label={intl.formatMessage(adminMessages.shippingPickupStreet)}
                onChange={(event) => patch({ address1: event.target.value })}
              />
            </div>

            <div className="gv-flex gv-flex-col gv-gap-md gv-w-full gv-tab-flex-row">
              <div className="gv-form-option gv-w-full">
                <label className="gv-label" htmlFor={`${fieldId}-city`}>
                  <FormattedMessage {...adminMessages.shippingPickupCity} />
                </label>
                <GeneralField
                  id={`${fieldId}-city`}
                  value={fields.city}
                  error={errors.cityError}
                  data-testid="pickup-form-city"
                  aria-label={intl.formatMessage(adminMessages.shippingPickupCity)}
                  onChange={(event) => patch({ city: event.target.value })}
                />
              </div>

              <div className="gv-form-option gv-w-full">
                <label className="gv-label" htmlFor={`${fieldId}-postcode`}>
                  <FormattedMessage {...adminMessages.shippingPickupPostcode} />
                </label>
                <GeneralField
                  id={`${fieldId}-postcode`}
                  value={fields.postcode}
                  error={errors.postcodeError}
                  data-testid="pickup-form-postcode"
                  aria-label={intl.formatMessage(adminMessages.shippingPickupPostcode)}
                  onChange={(event) => patch({ postcode: event.target.value })}
                />
              </div>
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={`${fieldId}-country`}>
                <FormattedMessage {...adminMessages.shippingPickupCountry} />
              </label>
              <div className="gv-input gv-input-select gv-w-full">
                <select
                  id={`${fieldId}-country`}
                  value={fields.country}
                  data-testid="pickup-form-country"
                  aria-label={intl.formatMessage(adminMessages.shippingPickupCountry)}
                  onChange={(event) => patch({ country: event.target.value })}
                >
                  <option value="">
                    {intl.formatMessage(adminMessages.shippingPickupCountryPlaceholder)}
                  </option>
                  {countryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Icon name="expand_more" size="small" />
              </div>
              {errors.countryError && (
                <span className="gv-text-sm gv-state-critical" role="alert">
                  {errors.countryError}
                </span>
              )}
            </div>
          </ContentContainer>
        </div>

        {saveError && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="pickup-form-error"
          >
            {saveError}
          </p>
        )}

        <FormFooter>
          <button
            type="button"
            className="gv-button gv-button-cancel"
            data-testid="pickup-form-cancel"
            onClick={handleCancel}
            disabled={saving}
          >
            <FormattedMessage {...adminMessages.formBack} />
          </button>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="pickup-form-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.shippingSaving} />
            ) : (
              <FormattedMessage {...adminMessages.shippingPickupSave} />
            )}
          </button>
        </FormFooter>
      </div>

      {confirmDiscard && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.shippingDiscardTitle)}
          message={intl.formatMessage(adminMessages.shippingDiscardMessage)}
          confirmLabel={intl.formatMessage(adminMessages.shippingDiscard)}
          cancelLabel={intl.formatMessage(adminMessages.shippingKeepEditing)}
          loadingLabel={intl.formatMessage(adminMessages.shippingDiscard)}
          confirmVariant="destructive"
          onPrimaryClick={() => {
            setConfirmDiscard(false);
            onCancel();
          }}
          onSecondaryClick={() => setConfirmDiscard(false)}
          onClose={() => setConfirmDiscard(false)}
        />
      )}
    </div>
  );
}
